/**
 * WebSerial Manager for PalmSens Device Communication
 * Handles device connection, disconnection, and data transmission using Web Serial API
 *
 * Note: Web Serial API works on Windows, macOS, Linux, and ChromeOS
 * Supported in Chrome, Edge, and Opera (not Firefox or Safari)
 */

import { getSerialConfig, isPicoDeviceByVidPid } from 'methodscript-communication';
import { ConnectionState } from 'methodscript-device';
import type { SerialDeviceInfo } from 'methodscript-device';
import { logger } from 'shared-monaco-methodscript';

export class WebSerialManager extends EventTarget {
    private port: SerialPort | null;
    private state: ConnectionState;
    private reader: ReadableStreamDefaultReader<string> | null;
    private writer: WritableStreamDefaultWriter<string> | null;
    private readableStreamClosed: Promise<void> | null;
    private writableStreamClosed: Promise<void> | null;
    private keepReading: boolean;
    private isSupported: boolean;

    constructor() {
        super();
        this.port = null;
        this.state = ConnectionState.Disconnected;
        this.reader = null;
        this.writer = null;
        this.readableStreamClosed = null;
        this.writableStreamClosed = null;
        this.keepReading = true;

        // Check Web Serial API support
        this.isSupported = 'serial' in navigator;
    }

    /**
     * Check if Web Serial API is supported in the current browser
     */
    isWebSerialSupported(): boolean {
        return this.isSupported;
    }

    /**
     * Get current connection state
     */
    getState(): ConnectionState {
        return this.state;
    }

    /**
     * Get current device info
     */
    getDeviceInfo(): SerialDeviceInfo | null {
        if (!this.port) {
            return null;
        }

        const info = this.port.getInfo();
        return {
            usbVendorId: info.usbVendorId,
            usbProductId: info.usbProductId,
            // Serial ports don't expose product/manufacturer names via Web Serial API
        };
    }

    /**
     * Set connection state and dispatch event
     */
    private setState(newState: ConnectionState): void {
        if (this.state === newState) {
            return;
        }

        this.state = newState;
        this.dispatchEvent(new CustomEvent('stateChange', { detail: { state: newState } }));
    }

    /**
     * Request device selection and connect
     */
    async connect(): Promise<void> {
        logger.debug('[WebSerial] Connect called, current state:', this.state);

        if (!this.isSupported) {
            throw new Error('Web Serial API is not supported in this browser. Please use Chrome, Edge, or Opera.');
        }

        if (this.state === ConnectionState.Connected) {
            logger.warn('[WebSerial] Already connected, disconnecting first...');
            await this.disconnect();
        }

        if (this.state === ConnectionState.Connecting) {
            throw new Error('Connection already in progress');
        }

        this.setState(ConnectionState.Connecting);

        try {
            // Request serial port from user
            logger.debug('[WebSerial] Requesting port selection...');
            this.port = await navigator.serial.requestPort({
                // Optional filters can be added if specific VID/PID are known
                // filters: [{ usbVendorId: 0x1234, usbProductId: 0x5678 }]
            });

            const info = this.port.getInfo();
            logger.debug('[WebSerial] Port selected:', {
                usbVendorId: info.usbVendorId ? '0x' + info.usbVendorId.toString(16) : 'N/A',
                usbProductId: info.usbProductId ? '0x' + info.usbProductId.toString(16) : 'N/A',
            });

            // Get device-specific serial configuration
            const configInput = {
                portInfo: {
                    usbVendorId: info.usbVendorId,
                    usbProductId: info.usbProductId,
                },
            };
            logger.debug('[WebSerial] Calling getSerialConfig with:', JSON.stringify(configInput, null, 2));
            const serialConfig = getSerialConfig(configInput);

            const isPico = isPicoDeviceByVidPid({ vendorId: info.usbVendorId ?? 0, productId: info.usbProductId ?? 0 });
            logger.debug('[WebSerial] Device type:', isPico ? 'Pico' : 'Standard');
            logger.debug('[WebSerial] Serial configuration:', JSON.stringify(serialConfig, null, 2));

            // Open the port with device-specific settings
            logger.debug('[WebSerial] Opening port with', serialConfig.baudRate, 'baud...');
            await this.port.open(serialConfig);
            logger.debug('[WebSerial] Port opened successfully');

            // Set up the text encoder/decoder for the streams
            const textDecoder = new TextDecoderStream();
            const textEncoder = new TextEncoderStream();

            // Pipe the port's readable stream through the decoder
            this.readableStreamClosed = this.port.readable!.pipeTo(textDecoder.writable as WritableStream<Uint8Array>);
            this.reader = textDecoder.readable.getReader();

            // Pipe the encoder through the port's writable stream
            this.writableStreamClosed = textEncoder.readable.pipeTo(this.port.writable!);
            this.writer = textEncoder.writable.getWriter();

            this.setState(ConnectionState.Connected);

            // Start reading from device
            logger.debug('[WebSerial] Starting read loop...');
            this.startReading();

            logger.debug('[WebSerial] Connection established successfully');
            this.dispatchEvent(new CustomEvent('connected', { detail: this.getDeviceInfo() }));
        } catch (error) {
            logger.error('[WebSerial] Connection failed:', error);
            if (error instanceof Error) {
                logger.error('[WebSerial] Error details:', {
                    name: error.name,
                    message: error.message,
                    stack: error.stack,
                });
            }
            this.setState(ConnectionState.Error);
            await this.cleanup();
            throw error;
        }
    }

    /**
     * Disconnect from device
     */
    async disconnect(): Promise<void> {
        if (this.state === ConnectionState.Disconnected) {
            return;
        }

        await this.cleanup();
        this.setState(ConnectionState.Disconnected);
        this.dispatchEvent(new CustomEvent('disconnected'));
    }

    /**
     * Clean up device resources
     */
    private async cleanup(): Promise<void> {
        logger.debug('[WebSerial] Cleanup called');

        // Stop reading
        this.keepReading = false;

        // Close reader
        try {
            if (this.reader) {
                logger.debug('[WebSerial] Canceling reader...');
                await this.reader.cancel();
                this.reader.releaseLock();
            }
        } catch (error) {
            logger.warn('[WebSerial] Error cancelling reader:', error);
        } finally {
            this.reader = null;
        }

        // Close writer
        try {
            if (this.writer) {
                logger.debug('[WebSerial] Closing writer...');
                await this.writer.close();
            }
        } catch (error) {
            logger.warn('[WebSerial] Error closing writer:', error);
        } finally {
            this.writer = null;
        }

        // Wait for streams to close
        try {
            if (this.readableStreamClosed) {
                await this.readableStreamClosed.catch(() => {
                    /* Ignore errors on close */
                });
            }
        } finally {
            this.readableStreamClosed = null;
        }

        try {
            if (this.writableStreamClosed) {
                await this.writableStreamClosed.catch(() => {
                    /* Ignore errors on close */
                });
            }
        } finally {
            this.writableStreamClosed = null;
        }

        // Close port
        try {
            if (this.port) {
                logger.debug('[WebSerial] Closing port...');
                await this.port.close();
                logger.debug('[WebSerial] Port closed');
            }
        } catch (error) {
            logger.warn('[WebSerial] Error closing port:', error);
        } finally {
            this.port = null;
        }
    }

    /**
     * Send MethodScript code to device
     */
    async sendScript(script: string): Promise<void> {
        logger.debug('[WebSerial] sendScript called');
        logger.debug('[WebSerial] Script length:', script.length, 'chars');
        logger.debug('[WebSerial] Script preview:', script.substring(0, 100));

        if (this.state !== ConnectionState.Connected || !this.port || !this.writer) {
            throw new Error('Not connected to a device');
        }

        try {
            // Write the script to the device
            logger.debug('[WebSerial] Writing', script.length, 'characters to device');
            await this.writer.write(script);
            logger.debug('[WebSerial] Script sent successfully');

            // Dispatch sent event
            this.dispatchEvent(
                new CustomEvent('dataSent', {
                    detail: {
                        data: script,
                        bytes: new TextEncoder().encode(script).length,
                    },
                }),
            );
        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : String(error);
            this.dispatchEvent(new CustomEvent('error', { detail: { message: errorMsg } }));
            throw new Error(`Failed to send script: ${errorMsg}`);
        }
    }

    /**
     * Start reading data from device
     */
    private async startReading(): Promise<void> {
        this.keepReading = true;

        try {
            while (this.keepReading && this.reader) {
                const { value, done } = await this.reader.read();

                if (done) {
                    logger.debug('[WebSerial] Read stream closed');
                    break;
                }

                if (value && value.length > 0) {
                    logger.debug('[WebSerial] Received:', value.length, 'characters');

                    // Dispatch received event
                    this.dispatchEvent(
                        new CustomEvent('dataReceived', {
                            detail: {
                                data: value,
                                bytes: new TextEncoder().encode(value).length,
                            },
                        }),
                    );
                }
            }
        } catch (error) {
            if (this.keepReading) {
                // Unrecoverable read error (e.g. device unplugged) — update state and clean up
                const errorMsg = error instanceof Error ? error.message : String(error);
                logger.error('[WebSerial] Read error:', errorMsg);
                this.keepReading = false;
                this.dispatchEvent(new CustomEvent('error', { detail: { message: errorMsg } }));
                await this.cleanup();
                this.setState(ConnectionState.Disconnected);
                this.dispatchEvent(new CustomEvent('disconnected'));
            }
        } finally {
            logger.debug('[WebSerial] Read loop ended');
        }
    }

    /**
     * Dispose of resources
     */
    dispose(): void {
        this.keepReading = false;
        void this.cleanup();
    }
}
